import React from "react";

/**
 * ParchmentBackground
 * ────────────────────
 * Renders a single site-wide aged-parchment texture layer. Mounted per-page
 * (opt-in) so pages that shouldn't get the texture (the homepage hero, any
 * legacy routes) can simply omit it.
 *
 * Positioning contract:
 *  - `position: fixed; inset: 0` — the layer covers the entire viewport and
 *    stays anchored during scroll, so pages of any height read as a single
 *    seamless wash. No `background-attachment: fixed` needed (which
 *    misbehaves on iOS Safari) — using a real fixed element sidesteps that.
 *  - `z-index: -1` — sits BEHIND all page content but IN FRONT of the html
 *    root's default paint, so any transparent/patterned regions of the page
 *    show it. Any opaque section (hero photos, dark nav, dark footer,
 *    parchment cards) naturally covers it.
 *  - `pointer-events: none` — pure decor; never intercepts clicks.
 *  - `#F2E3C2` fallback color is baked in so a slow texture download or
 *    404 collapses gracefully to the site's original flat parchment tone.
 *
 * The texture asset lives at `/textures/parchment.jpg` (1024×1024, 165 KB
 * JPEG, warm sepia, no vignette). Browsers cache it across all textured
 * pages after the first visit, so the cost is paid once per session.
 */
export default function ParchmentBackground() {
  // 2026-02 audit — the grainy sepia paper wash is already applied
  // site-wide by `html, body` in GlobalParchment.css (opaque sepia base
  // + top/bottom gradients + fractalNoise SVG at multiply blend). All
  // this component needs to do now is make sure it doesn't OCCLUDE that
  // body-level texture. So it renders nothing — every page inherits the
  // canonical body texture, no per-page background layering needed.
  // Kept as a mount point (rather than removed) so existing per-page
  // imports/JSX stay valid and future variations (dark hero pages, etc.)
  // can flip a variant here without touching every page.
  return null;
}
